import React, { useEffect, useRef } from "react";
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import { fontWeight } from "@mui/system";

// Datos constantes mientras se desarrolla el webservice
const defaultData = [
  {
    category: "January",
    value1: 1000,
    value2: 588,
    area1: 800,
    area2: 650,
  },
  {
    category: "February",
    value1: 1200,
    value2: 1800,
    area1: 950,
    area2: 1200,
  },
  {
    category: "March",
    value1: 850,
    value2: 1230,
    area1: 700,
    area2: 980,
  },
  {
    category: "April",
    value1: 1450,
    value2: 920,
    area1: 1100,
    area2: 750,
  },
  {
    category: "May",
    value1: 1650,
    value2: 1450,
    area1: 1300,
    area2: 1150,
  },
  {
    category: "June",
    value1: 1380,
    value2: 1680,
    area1: 1050,
    area2: 1350,
  },
  {
    category: "July",
    value1: 1780,
    value2: 1320,
    area1: 1450,
    area2: 1080,
  },
  {
    category: "August",
    value1: 1520,
    value2: 1580,
    area1: 1200,
    area2: 1280,
  },
  {
    category: "September",
    value1: 1890,
    value2: 1750,
    area1: 1550,
    area2: 1420,
  },
  {
    category: "October",
    value1: 2100,
    value2: 1980,
    area1: 1750,
    area2: 1650,
  },
  {
    category: "November",
    value1: 1750,
    value2: 2200,
    area1: 1420,
    area2: 1850,
  },
  {
    category: "December",
    value1: 2350,
    value2: 2150,
    area1: 1950,
    area2: 1780,
  },
];

const defaultMetadata = {
  categoryField: "category",
  columnFields: ["value1", "value2"], // Campos para columnas/barras
  areaFields: ["area1", "area2"], // Campos para áreas
  lineFields: [], // Nuevos campos para líneas
  columnSeriesNames: ["Sales Revenue", "Orders Count"],
  areaSeriesNames: ["Target Revenue", "Target Orders"],
  lineSeriesNames: [], // Nuevas series para líneas
  showLegend: true,
  showTooltip: true,
  showVerticalGridLines: true,
  showHorizontalGridLines: true,
  minValueAtZero: false,
  maxValueScaling: null,
};

export const XyChart = ({
  configuration,
  data: propData,
  metadata: propMetadata,
  showLegend = true,
  showTooltip = true,
  isPreview = false,
}) => {
  // Si viene configuration, armar data y metadata igual que el dashboard
  let data = propData;
  let metadata = propMetadata;
  if (configuration) {
    // Obtener xyConfig y xyColumns desde metadata o directamente
    const xyConfig =
      configuration.metadata?.xyConfig || configuration.xyConfig || {};
    const xyColumns = Array.isArray(configuration.metadata?.xyColumns)
      ? configuration.metadata.xyColumns
      : Array.isArray(configuration.xyColumns)
      ? configuration.xyColumns
      : [];
    // Obtener columnas desde columns, metadata.columns, o data
    const columns =
      Array.isArray(configuration.columns) && configuration.columns.length
        ? configuration.columns
        : Array.isArray(configuration.metadata?.columns) &&
          configuration.metadata.columns.length
        ? configuration.metadata.columns
        : configuration.data && configuration.data.length > 0
        ? Object.keys(configuration.data[0])
        : [];
    // Transformar los datos del array format al object format si es necesario
    data = Array.isArray(configuration.data)
      ? configuration.data.map((row) => {
          if (typeof row === "object" && !Array.isArray(row)) return row;
          const obj = {};
          columns.forEach((column, index) => {
            obj[column] = row[index];
          });
          return obj;
        })
      : [];
    // Construir metadata igual que en DashboardConfigurationService.js
    // Incluir columnas con renderType 'bar' y 'stacked_bars' para agrupadas
    const columnConfigs = xyColumns.filter(
      (col) => col.renderType === "bar" || col.renderType === "stacked_bars"
    );
    const areaConfigs = xyColumns.filter((col) => col.renderType === "area");
    const lineConfigs = xyColumns.filter((col) => col.renderType === "line");
    const columnFields = columnConfigs.map((col) => col.valueColumnName);
    const areaFields = areaConfigs.map((col) => col.valueColumnName);
    const lineFields = lineConfigs.map((col) => col.valueColumnName);
    const columnSeriesNames = columnConfigs.map(
      (col) => col.label || col.valueColumnName
    );
    const areaSeriesNames = areaConfigs.map(
      (col) => col.label || col.valueColumnName
    );
    const lineSeriesNames = lineConfigs.map(
      (col) => col.label || col.valueColumnName
    );
    const columnSeriesConfig = columnConfigs.map((col) => ({ ...col }));
    const areaSeriesConfig = areaConfigs.map((col) => ({ ...col }));
    const lineSeriesConfig = lineConfigs.map((col) => ({ ...col }));
    metadata = {
      categoryField: xyConfig.axisColumnName,
      columnFields,
      areaFields,
      lineFields,
      columnSeriesNames,
      areaSeriesNames,
      lineSeriesNames,
      columnSeriesConfig,
      areaSeriesConfig,
      lineSeriesConfig,
      showLegend: true,
      showTooltip: true,
      showVerticalGridLines: xyConfig.showVerticalGridLines,
      showHorizontalGridLines: xyConfig.showHorizontalGridLines,
      minValueAtZero: xyConfig.minValueAtZero,
      maxValueScaling: xyConfig.maxValueScaling,
      adjustableMinGrid: xyConfig.adjustableMinGrid,
      gridDistance: xyConfig.gridDistance,
      axisSync: xyConfig.axisSync,
    };
  }

  const chartRef = useRef(null);
  const rootRef = useRef(null);
  const chartInstanceRef = useRef(null);

  useEffect(() => {


    // Usar datos por defecto si no hay datos reales
    const chartData = data && data.length > 0 ? data : defaultData;
    const chartMetadata = metadata || defaultMetadata;

    // Extraer propiedades configurables desde metadata
    const showVerticalGridLines = chartMetadata.showVerticalGridLines;
    const showHorizontalGridLines = chartMetadata.showHorizontalGridLines;
    const minValueAtZero = chartMetadata.minValueAtZero === true;
    const maxValueScaling = chartMetadata.maxValueScaling ?? null;
    const adjustableMinGrid = chartMetadata.adjustableMinGrid === true;
    const gridDistance = chartMetadata.gridDistance ?? 20;
    const axisSync = chartMetadata.axisSync === true;

    // Solo crear el chart si no existe
    if (!rootRef.current && chartRef.current) {
      // Create root element
      rootRef.current = am5.Root.new(chartRef.current);

      // Set themes
      rootRef.current.setThemes([am5themes_Animated.new(rootRef.current)]);

      // Create chart
      chartInstanceRef.current = rootRef.current.container.children.push(
        am5xy.XYChart.new(rootRef.current, {
          panY: false,
          wheelY: "zoomX",
          layout: rootRef.current.verticalLayout,
        })
      );

      // Create Y-axis (left) para las barras/columnas
      const yAxisLeft = chartInstanceRef.current.yAxes.push(
        am5xy.ValueAxis.new(rootRef.current, {
          renderer: am5xy.AxisRendererY.new(rootRef.current, {
            opposite: false, // Lado izquierdo
          }),
          min: minValueAtZero ? 0 : undefined,
          max: maxValueScaling !== null ? maxValueScaling : undefined,
          adjustable: adjustableMinGrid,
        })
      );

      // Configurar colores y etiquetas del eje izquierdo
      yAxisLeft.get("renderer").labels.template.setAll({
        fill: am5.color("#646978"), // Color azul para distinguir
        fontSize: "10px",
      });
      // Configurar opacidad de las líneas de grid verticales
      yAxisLeft.get("renderer").grid.template.setAll({
        // strokeOpacity: showHorizontalGridLines ? 0.1 : 0,
        opacity: showHorizontalGridLines ? 1 : 0,
      });

      // Create Y-axis (right) para las áreas
      const yAxisRight = chartInstanceRef.current.yAxes.push(
        am5xy.ValueAxis.new(rootRef.current, {
          renderer: am5xy.AxisRendererY.new(rootRef.current, {
            opposite: true, // Lado derecho
          }),
          min: minValueAtZero ? 0 : undefined,
          max: maxValueScaling !== null ? maxValueScaling : undefined,
          syncWithAxis: axisSync ? yAxisLeft : undefined,
        })
      );

      // Configurar colores y etiquetas del eje derecho
      yAxisRight.get("renderer").labels.template.setAll({
        fill: am5.color("#646978"), // Color gris para distinguir
        fontSize: "10px",
      });

      // Configurar colores y etiquetas del eje derecho
      yAxisRight.get("renderer").grid.template.setAll({
        opacity: 0,
      });

      // Create X-Axis
      const xAxis = chartInstanceRef.current.xAxes.push(
        am5xy.CategoryAxis.new(rootRef.current, {
          maxDeviation: 0.2,
          renderer: am5xy.AxisRendererX.new(rootRef.current, {
            minGridDistance: gridDistance,
            cellStartLocation: 0.1,
            cellEndLocation: 0.9,
          }),
          categoryField: chartMetadata.categoryField || "category",
        })
      );

      // Configurar las etiquetas del eje X en diagonal y personalizar color/tamaño
      xAxis.get("renderer").labels.template.setAll({
        rotation: -45,
        centerY: am5.p50,
        centerX: am5.p100,
        paddingRight: 15,
        fontSize: "10px",
        fill: am5.color("#646978"),
      });
      // Configurar opacidad de las líneas de grid horizontales
      xAxis.get("renderer").grid.template.setAll({
        opacity: showVerticalGridLines ? 1 : 0,
      });

      // Set data for X-axis
      xAxis.data.setAll(chartData);

      // Create column series (barras) con config personalizada
      const columnFields = chartMetadata.columnFields || [];
      const columnSeriesNames =
        chartMetadata.columnSeriesNames ||
        columnFields.map((field, index) => `Column ${index + 1}`);
      const columnSeriesConfig = chartMetadata.columnSeriesConfig || [];

      columnFields.forEach((valueField, index) => {
        const config = columnSeriesConfig[index] || {};
        // Si la columna es 'stacked_bars', renderizar como Clustered Column Chart (agrupadas)
        const isStackedBars = config.renderType === "stacked_bars";
        const series = chartInstanceRef.current.series.push(
          am5xy.ColumnSeries.new(rootRef.current, {
            name: columnSeriesNames[index] || `Column ${index + 1}`,
            xAxis: xAxis,
            yAxis: yAxisLeft,
            valueYField: valueField,
            categoryXField: chartMetadata.categoryField || "category",
            clustered: true,
            stacked: false,
            tooltip: showTooltip
              ? am5.Tooltip.new(rootRef.current, {
                  labelText: "{name}: {valueY}",
                })
              : undefined,
          })
        );
        // thinBar: barras delgadas
        if (config.thinBar) {
          series.columns.template.setAll({ width: am5.percent(40) });
        }
        // barBorderRadius: radio de las esquinas
        if (config.barBorderRadius) {
          series.columns.template.setAll({
            cornerRadiusTL: config.barBorderRadius,
            cornerRadiusTR: config.barBorderRadius,
            cornerRadiusBL: 0,
            cornerRadiusBR: 0,
          });
        }
        // renderBullet: mostrar bullet personalizado
        if (config.renderBullet) {
          series.bullets.push(() => {
            return am5.Bullet.new(rootRef.current, {
              sprite: am5.Circle.new(rootRef.current, {
                radius: 5,
                fill: am5.color(0x29e286), // Puedes personalizar el color
                stroke: am5.color(0xffffff),
                strokeWidth: 2,
              }),
            });
          });
        }
        series.data.setAll(chartData);
      });

      // Create area series (áreas) con config personalizada
      const areaFields = chartMetadata.areaFields || [];
      const areaSeriesNames =
        chartMetadata.areaSeriesNames ||
        areaFields.map((field, index) => `Area ${index + 1}`);
      const areaSeriesConfig = chartMetadata.areaSeriesConfig || [];

      areaFields.forEach((valueField, index) => {
        const config = areaSeriesConfig[index] || {};
        const series = chartInstanceRef.current.series.push(
          am5xy.LineSeries.new(rootRef.current, {
            name: areaSeriesNames[index] || `Area ${index + 1}`,
            xAxis: xAxis,
            yAxis: yAxisRight,
            valueYField: valueField,
            categoryXField: chartMetadata.categoryField || "category",
            tooltip: showTooltip
              ? am5.Tooltip.new(rootRef.current, {
                  labelText: "{name}: {valueY}",
                })
              : undefined,
            ...(config.smoothLine ? { curveType: "monotoneX" } : {}),
          })
        );
        // dashedLine: línea discontinua (dashed)
        if (config.dashedLine) {
          series.strokes.template.setAll({ strokeDasharray: [5, 5] });
        }
        // renderBullet: mostrar bullet personalizado en áreas
        if (config.renderBullet) {
          series.bullets.push(() => {
            return am5.Bullet.new(rootRef.current, {
              sprite: am5.Circle.new(rootRef.current, {
                radius: 5,
                fill: am5.color(0x29e286),
                stroke: am5.color(0xffffff),
                strokeWidth: 2,
              }),
            });
          });
        }
        // Configure area fill
        series.fills.template.setAll({
          fillOpacity: 0.3,
          visible: true,
        });
        // Configure stroke (delgada si smoothLine)
        series.strokes.template.setAll({
          strokeWidth: config.smoothLine ? 1 : 2,
        });
        series.data.setAll(chartData);
      });

      // Create line series (líneas) con config personalizada
      const lineFields = chartMetadata.lineFields || [];
      const lineSeriesNames =
        chartMetadata.lineSeriesNames ||
        lineFields.map((field, index) => `Line ${index + 1}`);
      const lineSeriesConfig = chartMetadata.lineSeriesConfig || [];

      lineFields.forEach((valueField, index) => {
        const config = lineSeriesConfig[index] || {};
        const series = chartInstanceRef.current.series.push(
          am5xy.LineSeries.new(rootRef.current, {
            name: lineSeriesNames[index] || `Line ${index + 1}`,
            xAxis: xAxis,
            yAxis: yAxisRight,
            valueYField: valueField,
            categoryXField: chartMetadata.categoryField || "category",
            tooltip: showTooltip
              ? am5.Tooltip.new(rootRef.current, {
                  labelText: "{name}: {valueY}",
                })
              : undefined,
            ...(config.smoothLine ? { curveType: "monotoneX" } : {}),
          })
        );
        // Configuración especial para líneas
        series.strokes.template.setAll({
          strokeWidth: config.smoothLine ? 1 : 3,
        });
        if (config.dashedLine) {
          series.strokes.template.setAll({ strokeDasharray: [5, 5] });
        }
        if (config.renderBullet) {
          series.bullets.push(() => {
            return am5.Bullet.new(rootRef.current, {
              sprite: am5.Circle.new(rootRef.current, {
                radius: 5,
                fill: am5.color(0x29e286),
                stroke: am5.color(0xffffff),
                strokeWidth: 2,
              }),
            });
          });
        }
        series.data.setAll(chartData);
      });

      // Add legend if enabled (muestra todas las series de ambos ejes)
      if (
        showLegend &&
        (columnFields.length > 0 ||
          areaFields.length > 0 ||
          lineFields.length > 0)
      ) {
        const legend = chartInstanceRef.current.children.push(
          am5.Legend.new(rootRef.current, {
            maxWidth: chartRef.current?.offsetWidth || 250,
            width: am5.percent(100),
            y: am5.percent(100),
            centerY: am5.p100,
            paddingTop: 8,
            paddingBottom: 8,
          })
        );
        legend.data.setAll(chartInstanceRef.current.series.values);
        legend.labels.template.setAll({
          fontSize: isPreview ? "7px" : "10px",
          fontFamily: "Poppins, sans-serif",
          fill: am5.color("#646978"),
          oversizedBehavior: "wrap",
          fontWeight: "500",
        });
        legend.valueLabels.template.setAll({
          fontSize: isPreview ? "7px" : "10px",
          fontFamily: "Poppins, sans-serif",
          fill: am5.color("#646978"),
        });
        legend.markerRectangles.template.setAll({
          width: 16,
          height: 16,
        });
        legend.itemContainers.template.setAll({
          paddingTop: 2,
          paddingBottom: 8,
          paddingLeft: 4,
          paddingRight: 4,
          minWidth: 0,
          minHeight: 0,
        });
      }

      // Play initial animation
      chartInstanceRef.current.appear(1000, 100);
    }

    // Cleanup function
    return () => {
      if (rootRef.current) {
        rootRef.current.dispose();
        rootRef.current = null;
        chartInstanceRef.current = null;
      }
    };
  }, []); // Solo se ejecuta una vez al montar

  // Effect separado para actualizar datos
  useEffect(() => {
    if (chartInstanceRef.current && data && data.length > 0) {
      // Usar datos reales cuando estén disponibles
      const chartData = data;

      // Actualizar datos en X-axis
      const xAxis = chartInstanceRef.current.xAxes.getIndex(0);
      if (xAxis) {
        xAxis.data.setAll(chartData);
      }

      // Actualizar datos en todas las series
      chartInstanceRef.current.series.each((series) => {
        series.data.setAll(chartData);
      });
    }
  }, [data]); // Solo cuando cambian los datos

  // Estilos condicionales para el contenedor
  const containerStyle = isPreview
    ? {
        width: "100%",
        height: "100%",
        position: "relative",
        // paddingTop: "1.5rem",
        // paddingBottom: "3.5rem",
        minHeight: "250px",
        boxSizing: "border-box",
        overflow: "visible",
      }
    : {
        width: "100%",
        height: "100%",
        position: "relative",
        paddingTop: "0.5rem",
        boxSizing: "border-box",
        overflow: "visible",
      };

  return <div ref={chartRef} style={containerStyle} />;
};
